import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Play, Square, Radio, Wifi, Info, Captions, CaptionsOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiAuthRequestFilter } from '@/lib/shakaApiAuth';
import {
  channelsApi,
  getAccessToken,
  sessionsApi,
  type PlaybackSession,
  type SessionRoute,
} from '@/api/client';
import type { Channel } from '@/types';

// Import Shaka Player compiled build
import shaka from 'shaka-player/dist/shaka-player.experimental';

// CMCD v2 Response Mode plugin (loaded via <script> in index.html)
declare global {
  interface Window {
    responseModePlugin: {
      enableResponseMode(player: shaka.Player, config: { url: string; batchSize?: number; mode?: string }): void;
      disableResponseMode(): void;
      markPlayStart(): void;
      flush(): void;
    };
  }
}

// --- Types ---

interface PlayerStats {
  sessionId: string | null;
  cohort: string | null;
  transport: string;
  bufferLevel: number;
  liveLatency: number;
  resolution: string;
  bandwidth: string;
  activeSplices: number;
}

// A selectable caption/subtitle text track (WebVTT).
interface CaptionTrack {
  id: number;
  language: string;
  label: string | null;
}

// --- Component ---
//
// The session (sessionsApi, JSS#1015) serves ad routing and CMCD reporting,
// not playback: every session call may fail — a 401 once the token lapses,
// a 404 for a reaped session, a 5xx — and the player plays on without it.

export default function Player() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<shaka.Player | null>(null);

  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [transport, setTransport] = useState<'http' | 'moq'>('http');
  const [playing, setPlaying] = useState(false);
  const [session, setSession] = useState<PlaybackSession | null>(null);
  const [showStats, setShowStats] = useState(true);
  const [stats, setStats] = useState<PlayerStats>({
    sessionId: null,
    cohort: null,
    transport: 'http',
    bufferLevel: 0,
    liveLatency: 0,
    resolution: '-',
    bandwidth: '-',
    activeSplices: 0,
  });
  const [route, setRoute] = useState<SessionRoute | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [captionTracks, setCaptionTracks] = useState<CaptionTrack[]>([]);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionLang, setCaptionLang] = useState<string>('');

  // Fetch channels
  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: channelsApi.list,
    refetchInterval: 30_000,
  });

  // Auto-select first channel
  useEffect(() => {
    if (channels && channels.length > 0 && !selectedChannel) {
      setSelectedChannel(channels[0].id);
    }
  }, [channels, selectedChannel]);

  // Initialize Shaka Player
  useEffect(() => {
    shaka.polyfill.installAll();

    if (!shaka.Player.isBrowserSupported()) {
      setError('Browser not supported for Shaka Player');
      return;
    }

    const player = new shaka.Player();
    playerRef.current = player;
    // Manifests and segments under /api/ go through the BFF, which needs the
    // bearer; Shaka fetches them itself (JSS#1015).
    player.getNetworkingEngine()?.registerRequestFilter(apiAuthRequestFilter);

    (player as any).attach(videoRef.current!);

    player.addEventListener('error', (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.error('Shaka error:', detail);
      setError(`Player error: ${detail?.code ?? 'unknown'}`);
    });

    return () => {
      player.destroy();
      playerRef.current = null;
    };
  }, []);

  // Flush CMCD batch on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (window.responseModePlugin) {
        window.responseModePlugin.flush();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Heartbeat interval. A failed heartbeat is logged, never shown: playback
  // does not depend on it.
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(() => {
      sessionsApi.heartbeat(session.id)
        .catch((err: unknown) => console.warn('Session heartbeat failed:', err));
    }, 10_000);
    return () => clearInterval(interval);
  }, [session]);

  // Route polling. No route (a 404) and a failed call are both "no splices".
  useEffect(() => {
    if (!session || !playing) return;
    const interval = setInterval(async () => {
      const r = await sessionsApi.route(session.id).catch(() => null);
      setRoute(r);
      setStats((prev) => ({ ...prev, activeSplices: r?.splice_map?.length ?? 0 }));

      // Apply splice map as Shaka custom interstitials
      if (r?.splice_map?.length && playerRef.current) {
        for (const entry of r.splice_map) {
          try {
            playerRef.current.addCustomInterstitial({
              startTime: entry.at_group * 2, // approximate: group ~= 2 seconds
              endTime: (entry.at_group + entry.for_groups) * 2,
              uri: `/api/content/${entry.namespace}/master.m3u8`,
            });
          } catch {
            // Interstitial may already exist
          }
        }
      }
    }, 2_000);
    return () => clearInterval(interval);
  }, [session, playing]);

  // Stats update interval
  useEffect(() => {
    if (!playing || !playerRef.current) return;
    const interval = setInterval(() => {
      const player = playerRef.current;
      if (!player) return;
      const s = player.getStats();
      const buf = player.getBufferedInfo();
      const videoEl = videoRef.current;
      const bufEnd = buf.total.length > 0 ? buf.total[buf.total.length - 1].end : 0;
      const currentTime = videoEl?.currentTime ?? 0;
      setStats((prev) => ({
        ...prev,
        bufferLevel: Math.max(0, bufEnd - currentTime),
        liveLatency: s.liveLatency,
        resolution: s.width > 0 ? `${s.width}x${s.height}` : '-',
        bandwidth: s.estimatedBandwidth > 0
          ? `${(s.estimatedBandwidth / 1_000_000).toFixed(1)} Mbps`
          : '-',
      }));
    }, 1_000);
    return () => clearInterval(interval);
  }, [playing]);

  // Refresh the list of available caption/subtitle text tracks. Shaka surfaces
  // them from the HLS #EXT-X-MEDIA:TYPE=SUBTITLES rendition (http transport) or
  // the MSF catalog text track (moq transport) once the segment index is ready.
  const refreshCaptionTracks = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    let tracks: CaptionTrack[] = [];
    try {
      tracks = player.getTextTracks().map((t) => ({
        id: t.id,
        language: t.language,
        label: t.label,
      }));
    } catch {
      tracks = [];
    }
    setCaptionTracks((prev) => {
      // Avoid re-rendering when nothing changed (this runs on a 1s interval).
      if (prev.length === tracks.length &&
          prev.every((p, i) => p.id === tracks[i].id)) {
        return prev;
      }
      return tracks;
    });
    setCaptionLang((prev) => prev || (tracks[0]?.language ?? ''));
  }, []);

  const toggleCaptions = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const next = !captionsOn;
    try {
      if (next) {
        const tracks = player.getTextTracks();
        const chosen = tracks.find((t) => t.language === captionLang) ?? tracks[0];
        if (chosen) player.selectTextTrack(chosen);
      } else {
        // Shaka 5.x: passing null unloads the active text track (captions off).
        player.selectTextTrack(null);
      }
      setCaptionsOn(next);
    } catch (err) {
      console.error('Failed to toggle captions:', err);
    }
  }, [captionsOn, captionLang]);

  const changeCaptionLanguage = useCallback((lang: string) => {
    setCaptionLang(lang);
    const player = playerRef.current;
    if (!player) return;
    try {
      if (captionsOn) {
        const chosen = player.getTextTracks().find((t) => t.language === lang);
        if (chosen) player.selectTextTrack(chosen);
      }
    } catch (err) {
      console.error('Failed to select caption language:', err);
    }
  }, [captionsOn]);

  // Poll for caption tracks while playing (text tracks appear once Shaka builds
  // the segment index, which can lag the initial load) and reset on stop.
  useEffect(() => {
    if (!playing) {
      setCaptionTracks([]);
      setCaptionsOn(false);
      setCaptionLang('');
      return;
    }
    refreshCaptionTracks();
    const interval = setInterval(refreshCaptionTracks, 1_000);
    return () => clearInterval(interval);
  }, [playing, refreshCaptionTracks]);

  // Start playback
  const handlePlay = useCallback(async () => {
    if (!selectedChannel || !playerRef.current) return;
    setError(null);

    // Create the session. A failure used to abort Play with an error, so
    // while every session call was a 401 nothing played (JSS#1015). Without
    // a session the player plays with no ad route.
    //
    // On the channel's id, not the namespace loaded below: session-service
    // resolves the channel's stream itself and keys the session's ad routes
    // by it (JSS#1085), so the player need not know which name it is.
    let sess: PlaybackSession | null = null;
    try {
      sess = await sessionsApi.create(selectedChannel, transport);
    } catch (err) {
      console.warn('Playing without a session: creating one failed:', err);
    }
    setSession(sess);
    setStats((prev) => ({
      ...prev,
      sessionId: sess?.id ?? null,
      cohort: sess?.cohort ?? null,
      transport: sess?.transport ?? transport,
    }));

    try {
      // Enable CMCD v1 (native Shaka). With no session, '' makes Shaka
      // generate its own id; configure() merges, so leaving it out would keep
      // the previous play's session id.
      playerRef.current.configure({
        cmcd: {
          enabled: true,
          sessionId: sess?.id ?? '',
          contentId: selectedChannel,
        },
      });

      // Load manifest
      const channel = channels?.find((c: Channel) => c.id === selectedChannel);
      const config = channel?.config as Record<string, unknown> | undefined;
      // The channel's stream: its config.namespace, else its id. `||`, not
      // `??`: '' names no stream, as the services have it (db-common
      // resolveStreamChannel, channel.state's streamId), and with `??` it
      // loaded `/api/manifests//master.m3u8` (JSS#1079).
      const namespace = (config?.namespace as string | undefined) || selectedChannel;

      if (transport === 'http') {
        // HTTP path: HLS via manifest-service or Varnish
        const manifestUrl = `/api/manifests/${namespace}/master.m3u8`;
        await playerRef.current.load(manifestUrl);
      } else {
        // MOQ path: WebTransport via Shaka experimental MSF parser
        const moqRelayUrl = import.meta.env.VITE_MOQ_RELAY_URL ?? 'https://relay.jeket.com:4443/moq-relay';
        const token = getAccessToken();
        const moqUrl = token
          ? `${moqRelayUrl}/${namespace}?token=${token}`
          : `${moqRelayUrl}/${namespace}`;
        // Configure MSF namespace so Shaka knows which MOQ tracks to subscribe to
        (playerRef.current as any).configure({
          manifest: { msf: { namespaces: [namespace] } },
        });
        await (playerRef.current as any).load(moqUrl, undefined, 'application/msf');
      }

      // Enable CMCD v2 Response Mode plugin
      if (window.responseModePlugin) {
        window.responseModePlugin.markPlayStart();
        window.responseModePlugin.enableResponseMode(playerRef.current, {
          mode: 'json',
          batchSize: 8,
          url: '/api/cmcd/batch',
        });
      }

      videoRef.current?.play();
      setPlaying(true);
    } catch (err) {
      console.error('Play failed:', err);
      setError(`Failed to start: ${err instanceof Error ? err.message : String(err)}`);
      // Nothing plays, so end the session this Play created (JSS#1022). Left
      // set, it went on being heartbeated, with Play showing, until the page
      // closed or the next Play replaced it; then the reaper ended it.
      if (sess) {
        setSession(null);
        setStats((prev) => ({ ...prev, sessionId: null, cohort: null }));
        await sessionsApi.end(sess.id)
          .catch((endErr: unknown) => console.warn('Ending the session failed:', endErr));
      }
    }
  }, [selectedChannel, transport, channels]);

  // Stop playback
  const handleStop = useCallback(async () => {
    // Disable CMCD v2 plugin (flushes remaining batch)
    if (window.responseModePlugin) {
      window.responseModePlugin.disableResponseMode();
    }
    if (playerRef.current) {
      await playerRef.current.unload();
    }
    if (session) {
      await sessionsApi.end(session.id)
        .catch((err: unknown) => console.warn('Ending the session failed:', err));
    }
    setPlaying(false);
    setSession(null);
    setRoute(null);
    setStats({
      sessionId: null,
      cohort: null,
      transport: 'http',
      bufferLevel: 0,
      liveLatency: 0,
      resolution: '-',
      bandwidth: '-',
      activeSplices: 0,
    });
  }, [session]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-silver-bright">Player</h2>
        <p className="text-sm text-silver-muted mt-1">Preview a channel over HTTP or MoQ</p>
      </div>

      {/* Controls Bar */}
      <div className="flex flex-wrap items-center gap-4 bg-surface border border-surface-elevated rounded-xl p-4">
        {/* Channel Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-silver-muted">Channel</label>
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            disabled={playing}
            className="bg-surface-deep border border-surface-elevated rounded-lg px-3 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold disabled:opacity-50"
          >
            {!channels?.length && <option value="">No channels</option>}
            {channels?.map((ch: Channel) => (
              <option key={ch.id} value={ch.id}>
                {ch.name} ({ch.status})
              </option>
            ))}
          </select>
        </div>

        {/* Transport Toggle */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-silver-muted">Transport</label>
          <div className="flex rounded-lg overflow-hidden border border-surface-elevated">
            <button
              onClick={() => setTransport('http')}
              disabled={playing}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                transport === 'http'
                  ? 'bg-gold text-surface-deep'
                  : 'bg-surface-deep text-silver-muted hover:text-silver-bright',
                playing && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Wifi size={12} className="inline mr-1" />
              HTTP
            </button>
            <button
              onClick={() => setTransport('moq')}
              disabled={playing}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                transport === 'moq'
                  ? 'bg-gold text-surface-deep'
                  : 'bg-surface-deep text-silver-muted hover:text-silver-bright',
                playing && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Radio size={12} className="inline mr-1" />
              MOQ
            </button>
          </div>
        </div>

        {/* Play/Stop */}
        {!playing ? (
          <button
            onClick={handlePlay}
            disabled={!selectedChannel}
            className="flex items-center gap-2 px-4 py-1.5 bg-gold text-surface-deep rounded-lg text-sm font-medium hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            <Play size={14} />
            Play
          </button>
        ) : (
          <button
            onClick={handleStop}
            className="flex items-center gap-2 px-4 py-1.5 bg-status-error text-white rounded-lg text-sm font-medium hover:bg-red-500 transition-colors"
          >
            <Square size={14} />
            Stop
          </button>
        )}

        {/* Captions (only when the stream carries a text track) */}
        {captionTracks.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            {captionTracks.length > 1 && captionsOn && (
              <select
                value={captionLang}
                onChange={(e) => changeCaptionLanguage(e.target.value)}
                className="bg-surface-deep border border-surface-elevated rounded-lg px-2 py-1.5 text-xs text-silver-bright focus:outline-none focus:border-gold"
                title="Caption language"
              >
                {captionTracks.map((t) => (
                  <option key={t.id} value={t.language}>
                    {t.label ?? t.language}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={toggleCaptions}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                captionsOn
                  ? 'bg-gold/10 text-gold'
                  : 'text-silver-muted hover:text-silver-bright'
              )}
              title={captionsOn ? 'Turn captions off' : 'Turn captions on'}
              aria-pressed={captionsOn}
            >
              {captionsOn ? <Captions size={16} /> : <CaptionsOff size={16} />}
            </button>
          </div>
        )}

        {/* Stats Toggle */}
        <button
          onClick={() => setShowStats(!showStats)}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            captionTracks.length > 0 ? '' : 'ml-auto',
            showStats
              ? 'bg-gold/10 text-gold'
              : 'text-silver-muted hover:text-silver-bright'
          )}
          title="Toggle stats overlay"
        >
          <Info size={16} />
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-xl p-3 text-sm text-status-error">
          {error}
        </div>
      )}

      {/* Video Player */}
      <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
        <video
          ref={videoRef}
          className="w-full h-full"
          autoPlay={false}
          playsInline
          muted
        />

        {/* Stats Overlay (also while playing without a session) */}
        {showStats && (session || playing) && (
          <div className="absolute top-3 left-3 bg-black/80 border border-surface-elevated rounded-lg p-3 text-xs font-mono space-y-1 min-w-[220px]">
            <div className="flex justify-between">
              <span className="text-silver-muted">Session</span>
              <span className="text-silver-bright">{stats.sessionId ? `${stats.sessionId.slice(0, 8)}...` : 'none'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Cohort</span>
              <span className="text-gold">{stats.cohort ?? '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Transport</span>
              <span className="text-silver-bright">{stats.transport.toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Resolution</span>
              <span className="text-silver-bright">{stats.resolution}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Bandwidth</span>
              <span className="text-silver-bright">{stats.bandwidth}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Buffer</span>
              <span className={cn(
                stats.bufferLevel > 2 ? 'text-status-healthy' : stats.bufferLevel > 0.5 ? 'text-status-warning' : 'text-status-error'
              )}>
                {stats.bufferLevel.toFixed(1)}s
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Live Latency</span>
              <span className="text-silver-bright">{stats.liveLatency.toFixed(1)}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-silver-muted">Ad Splices</span>
              <span className={stats.activeSplices > 0 ? 'text-gold' : 'text-silver-muted'}>
                {stats.activeSplices}
              </span>
            </div>
          </div>
        )}

        {/* No-playback state */}
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/80">
            <div className="text-center">
              <Play size={48} className="text-silver-muted mx-auto mb-2" />
              <p className="text-sm text-silver-muted">Select a channel and press Play</p>
            </div>
          </div>
        )}
      </div>

      {/* Route Document Display */}
      {route && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-4">
          <h3 className="text-sm font-semibold text-silver-bright mb-2">Active Route Document</h3>
          <pre className="text-xs text-silver-muted font-mono overflow-x-auto">
            {JSON.stringify(route, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
